import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Database, GitBranch, Link2, Brain, Loader2, ClipboardList } from "lucide-react";
import { OriginSummary } from "../../lib/origin.jsx";
import { DATA_TABLES, DATA_DICTIONARY } from "../../lib/dataAdmin";
import { listDataTables } from "../../lib/dataTablesApi";
import { useRole } from "../../lib/role.jsx";
import { SectionHeader } from "../../components/Primitives";
import TableDrillDrawer from "../../components/datatables/TableDrillDrawer";
import CatalogRail from "../../components/explorer/CatalogRail";
import CatalogDetail from "../../components/explorer/CatalogDetail";
import { Lineage, Relationships, MlRegistry } from "../../components/explorer/ExplorerExtras";
import StandardsView from "../../components/standards/StandardsView";
import { cn } from "../../lib/utils";

const VIEWS = [["catalogue", "Catalogue", Database], ["lineage", "Lineage", GitBranch], ["relationships", "Relationships", Link2], ["ml", "ML Model Registry", Brain],
  ["standards", "Data Standards", ClipboardList]];
const STANDARDS_ROLES = ["it_head", "coo"];
const CATALOGUE_ROLES = ["ceo", "it_head", "sow_pilot"];
const STATIC = Object.fromEntries(DATA_TABLES.map(t => [t.name, t]));

function entry(t, dynamic) {
  const ref = STATIC[t.name] || {};
  const dict = DATA_DICTIONARY.filter(d => d.table === t.name).map(d => ({ key: d.column, type: d.type, pii: d.pii ? "pii" : null, description: d.description }));
  const columns = t.columns?.length ? t.columns : dict;
  const display = dynamic ? t.label : t.name;
  return { ...t, display, columns, powers: [].concat(ref.powers || []),
           search: [display, t.name, t.description, ...columns.map(c => `${c.key} ${c.description}`)].join(" ").toLowerCase() };
}

// Semantic Data Explorer: one read-only catalogue of every Bronze, Silver and Gold table, with real rows, lineage and relationships.
export default function SemanticExplorer() {
  const { role } = useRole();
  const [params] = useSearchParams();
  const canStd = STANDARDS_ROLES.includes(role?.id);
  const canCat = CATALOGUE_ROLES.includes(role?.id);
  const views = VIEWS.filter(([k]) => (k === "standards" ? canStd : canCat));
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState(() => (canStd && (params.get("view") === "standards" || !canCat) ? "standards" : "catalogue"));
  const [q, setQ] = useState("");
  const [layer, setLayer] = useState("all");
  const [selected, setSelected] = useState("fact_sales");
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!canCat) return undefined;
    let alive = true;
    let t;
    const load = () => listDataTables().then(x => { if (!alive) return; setData(x); setErr(null); if (x.preparing) t = setTimeout(load, 4000); }).catch(e => alive && setErr(e.message));
    load();
    return () => { alive = false; clearTimeout(t); };
  }, [open, canCat]);
  const entries = useMemo(() => (data ? [...data.tables.map(t => entry(t, false)), ...data.dynamic.map(t => entry(t, true))] : []), [data]);
  const dynamic = useMemo(() => entries.filter(e => e.name.startsWith("up.") || e.name.startsWith("bz.")), [entries]);
  const current = entries.find(e => e.name === selected) || entries[0];
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="Admin Console" title="Semantic Data Explorer"
        description={canCat ? "Browse every Bronze, Silver and Gold table: columns, descriptions, what each one powers and its real rows. Read-only."
          : "Data Standards: what every table the platform reads should contain, how the tables depend on each other, and templates to share with whoever prepares the files."} />
      <OriginSummary variant="system" />
      <div className="tile p-1.5 flex flex-wrap gap-1" data-testid="explorer-views">
        {views.map(([k, l, Icon]) => (
          <button key={k} data-testid={`explorer-view-${k}`} onClick={() => setView(k)}
                  className={cn("inline-flex items-center gap-2 px-4 h-9 rounded-sm text-sm transition-colors", view === k ? "bg-primary text-primary-fg font-semibold" : "text-muted hover:bg-surface-2 hover:text-fg")}>
            <Icon size={14} />{l}</button>))}
      </div>
      {view === "catalogue" && (!data ? <div className="tile p-6 text-sm text-muted" data-testid="explorer-loading">{err || <><Loader2 size={13} className="inline animate-spin mr-2" />Loading the catalogue…</>}</div> : (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4"><CatalogRail entries={entries} q={q} onQ={setQ} layer={layer} onLayer={setLayer} selected={current?.name} onSelect={setSelected} /></div>
          <div className="col-span-12 lg:col-span-8">{current && <CatalogDetail t={current} isIT={role?.id === "it_head"} onOpen={() => setOpen(current.name)} />}</div>
        </div>))}
      {view === "lineage" && <Lineage dynamic={dynamic} />}
      {view === "relationships" && <Relationships />}
      {view === "ml" && <MlRegistry />}
      {view === "standards" && canStd && <StandardsView initialTable={params.get("table")} initialCol={params.get("col")} />}
      {open && <TableDrillDrawer name={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
